import './editableTag.scss';
import React, {Fragment, useCallback, useState} from 'react';
import {SvgIcon} from '../../../../../icons/svgIcon';
import {Icons} from '../../../../../icons/icons';
import {useTranslation} from 'react-i18next';
import classNames from 'clsx';
import {Checkbox} from '../../../../../common/checkbox/checkbox';
import {FormFieldType, SubmittableInput} from '../../../../../common/submittable-input/submittableInput';
import {Spinner} from '../../../../../common/spinner/spinner';
import {useSearchURL} from '../../../../search-hooks';
import {URL_FILTER_PARAM} from '../../../../const';

type InputProps = {
    fieldType: FormFieldType;
    minDisplayHeight?: number;
    content: React.ReactNode;
    fieldName?: string;
    editableLabel?: string;
    allowEditing?: boolean;
    allowRemoval?: boolean;
    validated?: boolean;
    onTagRemoval?: () => void;
    onTagChange?: (newValue: string) => void;
    onTagValidation?: (value: string) => void;
    editModeActiveInitially?: boolean;
};

export const EditableTag = React.memo((props: InputProps) => {
    const {t} = useTranslation();
    const searchURL = useSearchURL();
    const CONTENT = props.content;
    const [editModeActive, changeEditModeTo] = useState<boolean>(props.editModeActiveInitially || false);
    const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
    const [deletingTag, setDeletingTag] = useState<boolean>(false);

    useCallback(() => {
        setLoadingUpdate(false);
        setDeletingTag(false);
        changeEditModeTo(false);
    }, []);

    const constructLink = (filterField: string): string => {
        const filter = `?${URL_FILTER_PARAM}=${filterField}[${props.editableLabel}]`;
        return encodeURI(`${searchURL}${filter}`);
    };

    const onEdit = () => {
        if (props.allowEditing && !loadingUpdate) {
            changeEditModeTo(true);
        }
    };

    const onSubmit = (newValue: string) => {
        if (props.onTagChange) {
            props.onTagChange(newValue);
        }
    };

    const onCancel = () => {
        changeEditModeTo(false);
        setDeletingTag(false);
    };

    const onDelete = () => {
        if (props.onTagRemoval) {
            props.onTagRemoval();
            setDeletingTag(true);
        }
    };

    let valueLink: string | undefined = undefined;
    if (props.fieldName === 'metadata.folder_dirPath') {
        valueLink = constructLink('metadata.folder_dirPath');
    } else if (props.fieldName === 'metadata.parentDocumentId_string') {
        valueLink = constructLink('id');
    }

    return (
        <div className="editable-tag">
            {props.fieldType === 'boolean' ? (
                <Checkbox
                    mayEdit={props.allowEditing || false}
                    value={Boolean(props.editableLabel === 'true')}
                    onChange={(value: boolean) => onSubmit(value.toString())}
                />
            ) : (
                <div
                    className={classNames(
                        'editable-field',
                        props.fieldType,
                        props.allowEditing || props.allowRemoval ? 'action-enabled' : '',
                        editModeActive ? 'edit-active' : '',
                        loadingUpdate ? 'loading-update' : ''
                    )}
                >
                    {editModeActive ? (
                        <SubmittableInput
                            fieldType={props.fieldType}
                            minDisplayHeight={props.minDisplayHeight}
                            initialValue={props.editableLabel || ''}
                            onSubmit={onSubmit}
                            onCancel={onCancel}
                            validated={props.validated}
                            onConfirm={props.onTagValidation}
                        />
                    ) : (
                        <Fragment>
                            {valueLink ?
                                <a href={valueLink} target="_blank" rel="noopener noreferrer"
                                   className="tag-link-icon">
                                    {CONTENT}
                                </a>
                                :
                                <div className="tag-content" onClick={onEdit}>
                                    {CONTENT}
                                </div>
                            }
                            <div className="tag-action">
                                {props.allowRemoval && !deletingTag && (
                                    <SvgIcon
                                        className="delete-icon"
                                        icon={Icons.REMOVE}
                                        onClick={onDelete}
                                        alt={t('search.details.remove')}
                                    />
                                )}
                                {deletingTag && (
                                    <div className={'delete-indicator'}>
                                        <Spinner/>
                                    </div>
                                )}
                            </div>
                        </Fragment>
                    )}
                </div>
            )}
        </div>
    );
});
